/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Grid,
  GridItem,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { FormProvider, SubmitHandler, useForm, useFormContext } from 'react-hook-form';

import { ContinuLogo } from '@/client/components/icons/ContinuIcons';
import HtmlRenderer from '@/client/components/html/HtmlRenderer';
import type { NotificationCreateInput } from '@/client/services/api/graphql/gql/graphql';
import SolidButton from '@/client/components/shared/buttons/SolidButton';
import TinyMceEditor from '@/client/components/admin/create/shared/TinyMceEditor';
import { useNotificationEditor } from '@/client/services/hooks/admin/notifications/useNotificationEditor';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

type EmailMessageFormValues = {
  from_name: string;
  subject: string;
  body: string;
};

interface AssignmentNotificationEmailMessageFormProps {
  handleCloseModal: () => void;
}

export default function AssignmentNotificationEmailMessageForm({
  handleCloseModal,
}: AssignmentNotificationEmailMessageFormProps) {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { parseTemplate } = useNotificationEditor();

  const { watch: parentFormWatch, setValue: parentFormSetValue } =
    useFormContext<NotificationCreateInput>();

  const { messaging } = parentFormWatch();

  const defaultFromName = messaging.email.find((setting) => setting.name === 'from_name')?.value;
  const defaultSubject = messaging.email.find((setting) => setting.name === 'subject')?.value;
  const defaultBody = messaging.email.find((setting) => setting.name === 'body')?.value;

  const methods = useForm<EmailMessageFormValues>({
    defaultValues: {
      from_name: defaultFromName,
      subject: defaultSubject,
      body: defaultBody,
    },
    mode: 'onChange',
  });

  const { register, watch, handleSubmit, reset } = methods;

  const { body } = watch();

  // TODO: Move these to useNotificationEditor hook
  const dynamicContentOptions = {
    due_in: t('notifications.dynamicContent.dueIn'),
    days_overdue: t('notifications.dynamicContent.daysOverdue'),
    assigned_content: t('notifications.dynamicContent.assignedContentList'),
    assigner_first_name: t('notifications.dynamicContent.assignerFirstName'),
    assigner_last_name: t('notifications.dynamicContent.assignerLastName'),
    assignee_first_name: t('notifications.dynamicContent.assigneeFirstName'),
    assignee_last_name: t('notifications.dynamicContent.assigneeLastName'),
    recipient_first_name: t('notifications.dynamicContent.recipientFirstName'),
    recipient_last_name: t('notifications.dynamicContent.recipientLastName'),
  };

  const onSubmit: SubmitHandler<EmailMessageFormValues> = (data) => {
    parentFormSetValue('messaging.email', [
      { name: 'from_name', value: data.from_name },
      { name: 'subject', value: data.subject },
      { name: 'body', value: data.body },
    ]);

    reset();
    handleCloseModal();
  };

  const onSubmitError = () =>
    setToast({
      show: true,
      status: 'error',
      title: t('notifications.errors'),
    });

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit, onSubmitError)}>
        <Box>
          <Grid templateColumns="repeat(4, 1fr)" gap={24} padding={8}>
            <GridItem colSpan={2}>
              <Stack spacing={6}>
                <Stack spacing={2}>
                  <Text variant="createLabel">{t('notifications.messaging.email.senderName')}</Text>

                  <Input
                    {...register('from_name')}
                    variant="create"
                    placeholder={`${t('notifications.messaging.email.senderName.placeholder')}`}
                  />
                </Stack>

                <Stack spacing={2}>
                  <Text variant="createLabel">{t('notifications.messaging.email.subject')}</Text>

                  <Input
                    {...register('subject')}
                    variant="create"
                    placeholder={`${t('notifications.messaging.email.subject.placeholder')}`}
                  />
                </Stack>

                <TinyMceEditor
                  formName="body"
                  isRequired
                  label={t('notifications.messaging.email.message')}
                  useMenu={false}
                  toolbarOpts="undo redo | blocks | forecolor | alignleft aligncenter alignright alignjustify | bold italic underline"
                  dynamicContentOptions={dynamicContentOptions}
                  minHeight={50}
                />
              </Stack>
            </GridItem>

            <GridItem colSpan={2}>
              <Box
                backgroundColor="warmNeutral.0"
                border="1px solid"
                borderColor="warmNeutral.200"
                borderRadius="32px"
                paddingX={6}
              >
                <ContinuLogo boxSize={44} />

                <HtmlRenderer html={parseTemplate(body, 'email')} />
              </Box>
            </GridItem>
          </Grid>

          <Flex
            width="full"
            borderTop="1px solid"
            borderColor="neutral.100"
            justifyContent="flex-end"
            paddingX={6}
            paddingY={4}
            position="sticky"
            bottom={0}
            backgroundColor="white"
          >
            <ButtonGroup>
              <Button
                variant="cancel"
                onClick={() => {
                  reset();
                  handleCloseModal();
                }}
              >
                {t('global.actions.cancel')}
              </Button>

              <SolidButton type="submit">
                {t('notifications.messaging.email.saveChanges')}
              </SolidButton>
            </ButtonGroup>
          </Flex>
        </Box>
      </form>
    </FormProvider>
  );
}
